// src/components/Admin/TrainerAdminPanel.js
import { useTranslation } from "../../i18n";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/TrainerAdminPanel.css";
import API_BASE_URL from '../../config/api';

/* ================= Config ================= */
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.__CONFIG__ && window.__CONFIG__.API_BASE) ||
  `${API_BASE_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15000,
});

/* ================= Auth ================= */
function extractToken(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^bearer\s+/i.test(raw)) return raw.replace(/^bearer\s+/i, "").trim();

  if (raw.startsWith("{") && raw.endsWith("}")) {
    try {
      const obj = JSON.parse(raw);
      const t = obj?.token || obj?.accessToken || obj?.jwt;
      if (t && String(t).trim()) return String(t).trim();
    } catch {}
  }
  return raw;
}

function pickTokenFromStorage() {
  const keys = ["token", "authToken", "accessToken", "jwt", "tzoneToken", "auth", "user"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    const t = extractToken(v);
    if (t) return t;
  }
  return null;
}

api.interceptors.request.use((config) => {
  const token = pickTokenFromStorage();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const normalizeStatus = (v) => {
  const s = (v ?? "").toString().toLowerCase();
  if (!s) return "activo";
  if (s === "blocked") return "bloqueado";
  if (s === "active") return "activo";
  return s;
};

const TrainerAdminPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [trainers, setTrainers] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    const role = (localStorage.getItem("role") || localStorage.getItem("userRole") || "").toLowerCase();
    if (!["admin", "moderator", "trainer_admin"].includes(role)) {
      alert(t("adminPanel.noPermission"));
      navigate("/login");
    }
  }, [navigate, t]);

  const fetchTrainers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/trainers");
      setTrainers(Array.isArray(data) ? data : data?.trainers || []);
    } catch (error) {
      console.error("❌ Error al obtener entrenadores:", error);
      alert(error?.response?.data?.message || "No se pudo cargar la lista de entrenadores.");
      setTrainers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return trainers;

    return trainers.filter((t) => {
      const name = `${t.firstName || ""} ${t.lastName || ""}`.trim().toLowerCase();
      const em = (t.email || "").toLowerCase();
      return name.includes(term) || em.includes(term);
    });
  }, [trainers, q]);

  const handleBlockToggle = async (trainerId, currentStatus) => {
    try {
      const status = normalizeStatus(currentStatus);
      if (status === "bloqueado") {
        await api.post(`/admin/users/${trainerId}/unblock`);
      } else {
        await api.post(`/admin/users/${trainerId}/block`);
      }
      await fetchTrainers();
    } catch (error) {
      console.error("❌ Error al bloquear/desbloquear entrenador:", error);
      alert(error?.response?.data?.message || "No se pudo cambiar el estado del entrenador.");
    }
  };

  const handleDelete = async (trainerId) => {
    if (!window.confirm(t("adminPanel.confirmDelete"))) return;

    try {
      await api.delete(`/admin/trainers/${trainerId}`);
      await fetchTrainers();
    } catch (error) {
      console.error("❌ Error al eliminar entrenador:", error);
      alert(error?.response?.data?.message || "No se pudo eliminar el entrenador.");
    }
  };

  const handleSendInvite = async () => {
    if (!email) return alert(t("adminPanel.enterEmail"));

    try {
      await api.post("/admin/invitations/send-trainer", { trainerEmail: email });
      alert("✅ Invitación enviada correctamente");
      setEmail("");
      await fetchTrainers();
    } catch (error) {
      console.error("❌ Error al enviar invitación:", error);
      alert(error?.response?.data?.message || "❌ Error al enviar la invitación");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <div className="admin-panel-container">
      <div
        className="admin-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.5rem",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0, color: "#193255" }}>{t("adminPanel.title")}</h2>

        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button className="btn-block" type="button" onClick={fetchTrainers} disabled={loading}>
            {loading ? "Cargando…" : "Refrescar"}
          </button>

          <button className="btn-delete" type="button" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </div>

      <div
        style={{
          background: "#fff",
          padding: "1rem",
          borderRadius: 12,
          marginBottom: "1rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <h5 style={{ marginTop: 0 }}>{t('athleteAdmin.inviteNewAthlete')}</h5>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("adminPanel.emailPlaceholder")}
            style={{ maxWidth: 420 }}
          />
          <button className="btn-block" onClick={handleSendInvite}>
            Enviar invitación
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <input
          className="form-control"
          placeholder={t("adminPanel.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 420 }}
        />
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>{t('athleteAdmin.name')}</th>
            <th>{t('athleteAdmin.email')}</th>
            <th>{t('athleteAdmin.status')}</th>
            <th>{t('athleteAdmin.actions')}</th>
          </tr>
        </thead>

        <tbody>
          {filtered.map((t) => {
            const id = t._id || t.id;
            const status = normalizeStatus(t.status);
            const fullName = `${t.firstName || ""} ${t.lastName || ""}`.trim() || "Entrenador";

            return (
              <tr key={id}>
                <td>{fullName}</td>
                <td>{t.email}</td>
                <td>{status}</td>
                <td>
                  <button
                    className="btn-block"
                    onClick={() => handleBlockToggle(id, status)}
                    style={{
                      backgroundColor: status === "bloqueado" ? "#2e7d32" : "#f4a300",
                      marginRight: 8,
                    }}
                  >
                    {status === "bloqueado" ? "Desbloquear" : "Bloquear"}
                  </button>

                  <button className="btn-delete" onClick={() => handleDelete(id)}>
                    Eliminar
                  </button>
                </td>
              </tr>
            );
          })}

          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan={4} style={{ textAlign: "center", color: "#666" }}>
                No hay entrenadores para mostrar.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TrainerAdminPanel;